/**
 * Multi-select filter (checkbox panel + Apply / Reset).
 *
 * - Draft selections while open; badge counts draft live.
 * - Close without Apply discards draft → applied (URL) state.
 * - Apply commits draft and submits the parent form.
 * - Reset clears this select only and submits immediately (other filters unchanged).
 * - Reset is always visible; disabled when draft and applied are both empty.
 * - Open/close: single expanding shell (team-selector max-height pattern).
 * - Only one MultiSelectFilter open at a time (native <select>-like).
 */
export default (initialSelected: Array<string | number> = []) => {
  // Initialize synchronously so x-model checkboxes match URL state on first paint
  // (init()-only assignment can leave boxes unchecked after Apply + reload).
  const selected = initialSelected.map(String)

  return {
    open: false,
    panelHeight: 0,
    applied: [...selected] as string[],
    draft: [...selected] as string[],

    init() {
      ;(this as any).$nextTick(() => {
        this.measurePanel()
      })
    },

    get count(): number {
      return this.draft.length
    },

    get canReset(): boolean {
      return this.draft.length > 0 || this.applied.length > 0
    },

    measurePanel() {
      const inner = (this as any).$refs?.panelInner as HTMLElement | undefined
      if (!inner) {
        return
      }
      this.panelHeight = inner.scrollHeight
    },

    toggle() {
      if (this.open) {
        this.close()
      }
      else {
        this.openPanel()
      }
    },

    openPanel() {
      // Notify siblings first so another open panel can close (discard its draft).
      ;(this as any).$dispatch('lhc-multi-select-open', { source: (this as any).$el })
      this.draft = [...this.applied]
      this.measurePanel()
      this.open = true
    },

    close() {
      if (!this.open) {
        return
      }
      this.open = false
      this.draft = [...this.applied]
    },

    apply() {
      this.applied = [...this.draft]
      this.open = false
      this.submitForm()
    },

    reset() {
      this.draft = []
      this.applied = []
      this.open = false
      this.submitForm()
    },

    submitForm() {
      const root = (this as any).$el as HTMLElement
      const form = root.closest('form') as HTMLFormElement | null
      if (!form) {
        return
      }

      // Build query from Alpine state (not DOM checkboxes): x-model can lag one tick
      // behind draft=[] on reset, so FormData would still contain the old values and
      // a fragment-only form action would only update the hash (no listing refresh).
      const params = new URLSearchParams()
      form.querySelectorAll<HTMLElement>('[data-filter-name]').forEach((el) => {
        const name = el.dataset.filterName
        if (!name) {
          return
        }
        const data = (window as any).Alpine?.$data(el) as {
          draft?: string[]
          applied?: string[]
        } | undefined
        const values = el === root
          ? (this.draft ?? [])
          : (data?.applied ?? data?.draft ?? [])
        values.forEach((value) => {
          params.append(name, value)
        })
      })

      const url = new URL(window.location.pathname, window.location.origin)
      const qs = params.toString()
      url.search = qs
      const action = form.getAttribute('action')
      if (action?.startsWith('#')) {
        url.hash = action.slice(1)
      }

      window.location.assign(url.href)
    },
  }
}
